import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import SelectWithAdd from "@/components/SelectWithAdd";
import SearchableSelect from "@/components/SearchableSelect";
import StatusBadge from "@/components/StatusBadge";
import BulkUploadButton from "@/components/BulkUploadButton";
import { useAuth } from "@/contexts/AuthContext";
import { Pencil, Plus, Trash2, Search, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  customer_id: "",
  password: "",
  address: "",
  country: "",
  city: "",
  email: "",
  phone: "",
  status: "Active",
  invoice_type: "Monthly",
};

export default function CustomersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCol, setShowCol] = useState({}); // row id -> true if password visible

  const load = () => api.get("/customers").then((r) => setItems(r.data));

  // Load customers + countries on mount. `load` and `api.get` are stable refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    api.get("/geo/countries").then((r) => setCountries(r.data));
  }, []);

  useEffect(() => {
    if (!form.country) {
      setCities([]);
      return;
    }
    api.get(`/geo/cities?country_code=${form.country}`).then((r) => setCities(r.data));
  }, [form.country]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      customer_id: row.customer_id || "",
      password: row.password || "",
      address: row.address || "",
      country: row.country || "",
      city: row.city || "",
      email: row.email || "",
      phone: row.phone || "",
      status: row.status || "Active",
      invoice_type: row.invoice_type || "Monthly",
    });
    setShowPassword(false);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.country || !form.city) {
      toast.error("Name, Country and City are required");
      return;
    }
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(`Customer ${editing ? "updated" : "created"}`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete customer "${row.name}"?`)) return;
    try {
      await api.delete(`/customers/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const filtered = items.filter((r) => {
    const f = filter.toLowerCase();
    return (
      !f ||
      r.name?.toLowerCase().includes(f) ||
      r.customer_id?.toLowerCase().includes(f) ||
      r.email?.toLowerCase().includes(f) ||
      r.phone?.toLowerCase().includes(f) ||
      r.city?.toLowerCase().includes(f)
    );
  });

  return (
    <div data-testid="customers-page">
      <PageHeader
        overline="Masters"
        title="Customer Details"
        description="Manage your customer directory. Cities are filtered by the chosen country."
        actions={
          <div className="flex gap-2">
            <BulkUploadButton
              templateUrl="/customers/sample-template"
              uploadUrl="/customers/bulk-upload"
              templateName="customers_template.xlsx"
              label="Bulk Upload"
              testIdPrefix="customer-bulk"
              onDone={load}
            />
            <Button
              data-testid="customer-add-button"
              onClick={openNew}
              className="rounded-none vsm-primary-bg hover:opacity-90"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Customer
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 vsm-muted" />
            <Input
              data-testid="customer-search-input"
              className="rounded-none pl-9"
              placeholder="Search by name, customer ID, email, phone, city…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="vsm-overline ml-auto">{filtered.length} result(s)</div>
        </div>

        <div className="border vsm-border vsm-surface overflow-x-auto vsm-table-scroll" data-testid="customers-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b vsm-border text-left">
                <Th>Customer ID</Th>
                <Th>Name</Th>
                <Th>Password</Th>
                <Th>Country / City</Th>
                <Th>Phone</Th>
                <Th>Email</Th>
                <Th>Status</Th>
                <Th className="w-28 text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 vsm-muted text-center">No customers yet.</td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b vsm-border hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                  <Td className="font-mono text-xs">{r.customer_id || "—"}</Td>
                  <Td className="font-semibold">{r.name}</Td>
                  <Td className="font-mono text-xs">
                    {r.password ? (
                      <button
                        type="button"
                        onClick={() => setShowCol((s) => ({ ...s, [r.id]: !s[r.id] }))}
                        className="inline-flex items-center gap-1.5 hover:underline"
                        data-testid={`customer-toggle-pw-${r.id}`}
                      >
                        <span>{showCol[r.id] ? r.password : "••••••"}</span>
                        {showCol[r.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </button>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>{r.country} · {r.city}</Td>
                  <Td>{r.phone || "—"}</Td>
                  <Td>{r.email || "—"}</Td>
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td className="text-right">
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`customer-edit-${r.id}`}
                      className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => remove(r)}
                        data-testid={`customer-delete-${r.id}`}
                        className="p-1.5 hover:bg-red-100 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border vsm-border max-w-xl" data-testid="customer-dialog">
          <DialogHeader>
            <div className="vsm-overline">{editing ? "Edit" : "Create"}</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {editing ? "Edit Customer" : "New Customer"}
            </DialogTitle>
            <DialogDescription className="vsm-muted text-sm">
              Country list contains all ISO countries; cities filter by selection.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={save} className="grid grid-cols-2 gap-4 mt-2">
            <Field label="Customer ID">
              <Input
                data-testid="customer-customer-id"
                className="rounded-none font-mono"
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                placeholder="Optional unique ID (e.g. CUST-100)"
              />
            </Field>
            <Field label="Customer Name" required>
              <Input data-testid="customer-name" className="rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </Field>
            <Field label="Password" className="sm:col-span-2">
              <div className="relative">
                <Input
                  data-testid="customer-password"
                  type={showPassword ? "text" : "password"}
                  className="rounded-none font-mono pr-10"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Customer portal password (optional)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 vsm-muted hover:opacity-80"
                  data-testid="customer-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
            <Field label="Country" required>
              <SearchableSelect
                testId="customer-country"
                value={form.country}
                onChange={(v) => setForm({ ...form, country: v, city: "" })}
                options={countries.map((c) => ({ id: c.code, label: c.name }))}
                placeholder="Select country"
              />
            </Field>
            <Field label="City" required>
              <SearchableSelect
                testId="customer-city"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
                options={cities.map((c) => ({ id: c.name, label: c.name }))}
                placeholder={form.country ? "Select city" : "Select country first"}
                disabled={!form.country}
                emptyMessage="No cities for this country"
              />
            </Field>
            <Field label="Phone">
              <Input data-testid="customer-phone" className="rounded-none" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input data-testid="customer-email" type="email" className="rounded-none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Address" className="sm:col-span-2">
              <Textarea data-testid="customer-address" className="rounded-none" rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
            <Field label="Status">
              <SelectWithAdd
                testId="customer-status"
                value={form.status || "Active"}
                onChange={(v) => setForm({ ...form, status: v })}
                options={[{ id: "Active", label: "Active" }, { id: "Inactive", label: "Inactive" }]}
              />
            </Field>
            <Field label="Invoice Type">
              <SelectWithAdd
                testId="customer-invoice-type"
                value={form.invoice_type || "Monthly"}
                onChange={(v) => setForm({ ...form, invoice_type: v })}
                options={[{ id: "Monthly", label: "Monthly" }, { id: "Yearly", label: "Yearly" }]}
              />
            </Field>

            <DialogFooter className="sm:col-span-2 mt-2">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="customer-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-none vsm-primary-bg hover:opacity-90" data-testid="customer-save">
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 vsm-overline font-semibold ${className}`}>{children}</th>
);
export const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="vsm-overline">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
